/**
 * Carrier Users management — Settings dialog tab.
 *
 * ⭐ F212 (migration 244, Decision 366) — NOW "PORTAL USERS": CARRIERS AND CLIENTS.
 * > Pete, 2026-10-01: *"We already have Carrier Portal Users in Dock Scheduler. Should be
 * > modified to include carriers and clients."*
 * Invite and Approve each pick Carrier or Client. A client user is one client code of the
 * scheduler's own company (the server takes the company from his token); it signs in with
 * the same magic link and lands on the client portal (/client).
 *
 * Panels: Invite, Pending Approvals, Reactivation Requests, Approved Portal Users, and
 * (F215) Disabled Portal Users.
 *
 * ⭐ F215 (migration 245, Decision 367) — ONE EMAIL, SEVERAL ACCOUNTS.
 * > Pete, 2026-10-01: *"We need to allow the same email across clients/carriers"* ·
 * > *"And now I can un-disable the account"*.
 * One row per account (a carrier, or one client of this company); the same email may hold
 * several. Invite refuses only the SAME account, and says to turn it back on below when it is
 * disabled. Disabled Portal Users lists every disabled row this scheduler may see (another
 * company's client users never appear) with Reactivate.
 *
 * Pending row workflow:
 *   - Scheduler picks a Carrier from the dropdown
 *   - Dropdown ends with "+ Create new Carrier..." which expands an inline form
 *     (carrierCode + carrierName) → POSTs to /mgmt/carriers/create → the new
 *     Carrier becomes the selection and the dropdown re-populates
 *   - Approve button sends PATCH /approve { carrierId }
 *   - Reject button sends DELETE (soft-delete; contact stays in DB but inactive)
 *
 * Approved row: shows email, display name, carrier code, last login.
 * Disable button soft-deletes. No edit-in-place (would require a separate flow).
 */

import { useEffect, useState, useCallback } from 'react';
import { Loader2, Check, X, UserPlus, Plus, AlertCircle, Shield, RotateCcw, Ban, Pencil, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
    listPendingCarrierContacts,
    listApprovedCarrierContacts,
    createCarrierContact,
    approveCarrierContact,
    disableCarrierContact,
    reactivateCarrierContact,
    listDisabledCarrierContacts,
    sendCarrierContactLink,
    updateCarrierContactPhone,
    adminListCarriers,
    adminCreateCarrier,
    listReactivationRequests,
    reactivateFromRequest,
    dismissReactivationRequest,
    blockFromRequest,
    getLookups,
    CarrierContactRow,
    CarrierAdminRow,
    ReactivationRequestRow as ReactivationRequest,
} from '@/api/client';
import { ConfirmDialog } from '@/components/ConfirmDialog';

/** Shape for an in-app confirmation pending the user's decision. */
interface PendingConfirm {
    title: string;
    message: string;
    confirmLabel: string;
    destructive?: boolean;
    onConfirm: () => void;
}

/** ⭐ F212: the clients a scheduler may give a client user (the scheduler dropdown's list). */
interface ClientOption { ClientCode: string; ClientName: string }
type PortalKind = 'Carrier' | 'Client';

function KindPicker({ value, onChange, disabled }: { value: PortalKind; onChange: (k: PortalKind) => void; disabled?: boolean }) {
    return (
        <div className="inline-flex rounded-md border overflow-hidden" role="radiogroup" aria-label="Portal">
            {(['Carrier', 'Client'] as const).map((k) => (
                <button key={k} type="button" role="radio" aria-checked={value === k} disabled={disabled}
                    onClick={() => onChange(k)}
                    className={`px-3 py-1 text-xs ${value === k ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted'}`}>
                    {k}
                </button>
            ))}
        </div>
    );
}

function ClientPicker({ clients, value, onChange, disabled }: {
    clients: ClientOption[]; value: string | null; onChange: (code: string | null) => void; disabled?: boolean;
}) {
    return (
        <Select value={value ?? ''} onValueChange={(v) => onChange(v || null)} disabled={disabled}>
            <SelectTrigger className="h-9"><SelectValue placeholder="Select a client..." /></SelectTrigger>
            <SelectContent>
                {clients.map((c) => (
                    <SelectItem key={c.ClientCode} value={c.ClientCode}>{c.ClientName || c.ClientCode} ({c.ClientCode})</SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}

/** What a portal user is linked to, in words. */
function linkedTo(c: CarrierContactRow): string {
    if (c.kind === 'Client' || c.clientCode) return `Client · ${c.clientName || c.clientCode}`;
    return c.carrierCode ? `Carrier · ${c.carrierCode}` : 'not linked';
}

interface CarrierUsersSectionProps {
    getToken: () => Promise<string>;
    onChanged: () => void;
}

export function CarrierUsersSection({ getToken, onChanged }: CarrierUsersSectionProps) {
    const [pending, setPending] = useState<CarrierContactRow[] | null>(null);
    const [approved, setApproved] = useState<CarrierContactRow[] | null>(null);
    const [reactivationRequests, setReactivationRequests] = useState<ReactivationRequest[] | null>(null);
    const [disabled, setDisabled] = useState<CarrierContactRow[] | null>(null);
    const [carriers, setCarriers] = useState<CarrierAdminRow[]>([]);
    const [clients, setClients] = useState<ClientOption[]>([]);
    const [loadError, setLoadError] = useState<string | null>(null);
    /** ⭐ F221: what the last invite / approve / send-link did with the email */
    const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
    const mailNotice = useCallback((email: string, sent: boolean | undefined, what: string) => {
        setNotice(sent
            ? { ok: true, text: `${what}. A welcome email with a sign-in link went to ${email}.` }
            : { ok: false, text: `${what}, but the welcome email could not be sent to ${email}. Use "Email link" below, or ask them to sign in at the portal login page.` });
    }, []);

    const reload = useCallback(async () => {
        setLoadError(null);
        try {
            const token = await getToken();
            const [p, a, c, r] = await Promise.all([
                listPendingCarrierContacts(token),
                listApprovedCarrierContacts(token),
                adminListCarriers(token),
                listReactivationRequests(token),
            ]);
            setPending(p.contacts);
            setApproved(a.contacts);
            setCarriers(c);
            setReactivationRequests(r.requests);
            // ⭐ F212: the client list for client users (a failure leaves carriers working)
            try { setClients((await getLookups(token)).clients ?? []); } catch { setClients([]); }
            // ⭐ F215: the disabled list (a failure leaves the rest working)
            try { setDisabled((await listDisabledCarrierContacts(token)).contacts); } catch { setDisabled([]); }
        } catch (e: any) {
            setLoadError(e.message ?? 'Failed to load carrier users');
        }
    }, [getToken]);

    useEffect(() => { reload(); }, [reload]);

    /** Called after a sub-component does something that needs a fresh fetch. */
    const handleChange = async () => {
        await reload();
        onChanged();
    };

    return (
        <div className="space-y-6">
            {loadError && (
                <div className="text-sm rounded-md p-3 bg-pipeline-rose-bg text-pipeline-rose-text border border-pipeline-rose-border flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span>{loadError}</span>
                </div>
            )}

            {notice && (
                <div className={`text-sm rounded-md p-3 flex items-start gap-2 border ${notice.ok ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-amber-50 text-amber-800 border-amber-200'}`}>
                    {notice.ok ? <Check className="h-4 w-4 flex-shrink-0 mt-0.5" /> : <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />}
                    <span className="flex-1">{notice.text}</span>
                    <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss" className="opacity-60 hover:opacity-100"><X className="h-4 w-4" /></button>
                </div>
            )}

            {/* Invite a new carrier directly */}
            <InvitePanel
                getToken={getToken}
                carriers={carriers}
                clients={clients}
                onCarriersChanged={async () => {
                    const t = await getToken();
                    const c = await adminListCarriers(t);
                    setCarriers(c);
                }}
                onInvited={handleChange}
                onMail={mailNotice}
            />

            {/* Pending */}
            <PendingPanel
                pending={pending}
                carriers={carriers}
                clients={clients}
                getToken={getToken}
                onCarriersChanged={async () => {
                    const t = await getToken();
                    const c = await adminListCarriers(t);
                    setCarriers(c);
                }}
                onChanged={handleChange}
                onMail={mailNotice}
            />

            {/* Reactivation Requests (disabled / blocked carriers who tried to sign in) */}
            <ReactivationRequestsPanel
                requests={reactivationRequests}
                getToken={getToken}
                onChanged={handleChange}
            />

            {/* Approved */}
            <ApprovedPanel
                approved={approved}
                getToken={getToken}
                onChanged={handleChange}
                onMail={mailNotice}
            />

            {/* ⭐ F215: Disabled, with Reactivate */}
            <DisabledPanel
                disabled={disabled}
                getToken={getToken}
                onChanged={handleChange}
            />
        </div>
    );
}

// =========================================================================
// Invite a new carrier directly (scheduler-invite path)
// =========================================================================

function InvitePanel({
    getToken,
    carriers,
    clients,
    onCarriersChanged,
    onInvited,
    onMail,
}: {
    getToken: () => Promise<string>;
    carriers: CarrierAdminRow[];
    clients: ClientOption[];
    onCarriersChanged: () => Promise<void>;
    onInvited: () => Promise<void>;
    onMail: (email: string, sent: boolean | undefined, what: string) => void;
}) {
    const [email, setEmail] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [kind, setKind] = useState<PortalKind>('Carrier');
    const [carrierId, setCarrierId] = useState<number | null>(null);
    const [clientCode, setClientCode] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const linked = kind === 'Carrier' ? carrierId !== null : !!clientCode;
    const canSubmit = email.trim().length > 0 && linked && !busy;

    const submit = async () => {
        if (!canSubmit) return;
        setBusy(true);
        setError(null);
        try {
            const token = await getToken();
            const sentTo = email.trim();
            const res = await createCarrierContact(token, {
                email: email.trim(),
                displayName: displayName.trim() || null,
                ...(kind === 'Carrier' ? { carrierId: carrierId as number } : { clientCode: clientCode as string }),
            });
            setEmail('');
            setDisplayName('');
            setCarrierId(null);
            setClientCode(null);
            await onInvited();
            onMail(sentTo, res.emailSent, `Invited ${sentTo}`);
        } catch (e: any) {
            setError(e.message ?? 'Failed to invite carrier');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div>
            <h3 className="font-medium text-sm mb-3">Invite a Portal User</h3>
            <div className="rounded-md border p-4 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                        <Label htmlFor="invite-email" className="text-xs">Email</Label>
                        <Input
                            id="invite-email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="dispatcher@acme-trucking.com"
                            disabled={busy}
                        />
                    </div>
                    <div>
                        <Label htmlFor="invite-name" className="text-xs">Display name (optional)</Label>
                        <Input
                            id="invite-name"
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            placeholder="Jane Smith"
                            disabled={busy}
                        />
                    </div>
                </div>

                <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                        <Label className="text-xs">Portal</Label>
                        <KindPicker value={kind} onChange={setKind} disabled={busy} />
                    </div>
                    {kind === 'Carrier' ? (
                        <CarrierPicker
                            carriers={carriers}
                            value={carrierId}
                            onChange={setCarrierId}
                            getToken={getToken}
                            onCarriersChanged={onCarriersChanged}
                            disabled={busy}
                        />
                    ) : (
                        <ClientPicker clients={clients} value={clientCode} onChange={setClientCode} disabled={busy} />
                    )}
                </div>

                {error && (
                    <div className="text-xs text-pipeline-rose-text bg-pipeline-rose-bg border border-pipeline-rose-border rounded p-2">
                        {error}
                    </div>
                )}

                <div className="flex justify-end">
                    <Button onClick={submit} disabled={!canSubmit} size="sm">
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />}
                        Invite
                    </Button>
                </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
                The portal user is created as approved immediately. A carrier signs in at /carrier,
                a client at /client, with their email address — the same emailed sign-in link.
            </p>
        </div>
    );
}

// =========================================================================
// Pending Approvals panel
// =========================================================================

function PendingPanel({
    pending,
    carriers,
    clients,
    getToken,
    onCarriersChanged,
    onChanged,
    onMail,
}: {
    pending: CarrierContactRow[] | null;
    carriers: CarrierAdminRow[];
    clients: ClientOption[];
    getToken: () => Promise<string>;
    onCarriersChanged: () => Promise<void>;
    onChanged: () => Promise<void>;
    onMail: (email: string, sent: boolean | undefined, what: string) => void;
}) {
    return (
        <div>
            <h3 className="font-medium text-sm mb-3 flex items-center gap-2">
                Pending Approvals
                {pending && pending.length > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-pipeline-rose-text text-white text-[10px] font-semibold">
                        {pending.length}
                    </span>
                )}
            </h3>

            {pending === null ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-3">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading...
                </div>
            ) : pending.length === 0 ? (
                <div className="text-sm text-muted-foreground py-3 px-3 border rounded-md">
                    No pending portal sign-ups.
                </div>
            ) : (
                <div className="border rounded-md divide-y">
                    {pending.map((c) => (
                        <PendingRow
                            key={c.carrierContactId}
                            contact={c}
                            carriers={carriers}
                            clients={clients}
                            getToken={getToken}
                            onCarriersChanged={onCarriersChanged}
                            onChanged={onChanged}
                            onMail={onMail}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function PendingRow({
    contact,
    carriers,
    clients,
    getToken,
    onCarriersChanged,
    onChanged,
    onMail,
}: {
    contact: CarrierContactRow;
    carriers: CarrierAdminRow[];
    clients: ClientOption[];
    getToken: () => Promise<string>;
    onCarriersChanged: () => Promise<void>;
    onChanged: () => Promise<void>;
    onMail: (email: string, sent: boolean | undefined, what: string) => void;
}) {
    const [kind, setKind] = useState<PortalKind>('Carrier');
    const [chosenCarrierId, setChosenCarrierId] = useState<number | null>(null);
    const [chosenClient, setChosenClient] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null);
    const chosen = kind === 'Carrier' ? chosenCarrierId !== null : !!chosenClient;

    const approve = async () => {
        if (!chosen) {
            setError(kind === 'Carrier' ? 'Pick a carrier first.' : 'Pick a client first.');
            return;
        }
        setBusy(true);
        setError(null);
        try {
            const token = await getToken();
            const res = await approveCarrierContact(token, contact.carrierContactId,
                kind === 'Carrier' ? { carrierId: chosenCarrierId as number } : { clientCode: chosenClient as string });
            await onChanged();
            onMail(contact.email, res.emailSent, `Approved ${contact.email}`);
        } catch (e: any) {
            setError(e.message ?? 'Approve failed');
        } finally {
            setBusy(false);
        }
    };

    const runReject = async () => {
        setBusy(true);
        setError(null);
        try {
            const token = await getToken();
            await disableCarrierContact(token, contact.carrierContactId);
            await onChanged();
        } catch (e: any) {
            setError(e.message ?? 'Reject failed');
        } finally {
            setBusy(false);
        }
    };

    const reject = () => setPendingConfirm({
        title: 'Reject signup?',
        message: `Reject signup from ${contact.email}? They will need to sign up again to request access.`,
        confirmLabel: 'Reject signup',
        destructive: true,
        onConfirm: runReject,
    });

    return (
        <div className="px-3 py-3 space-y-2">
            {pendingConfirm && (
                <ConfirmDialog
                    open={true}
                    title={pendingConfirm.title}
                    message={pendingConfirm.message}
                    confirmLabel={pendingConfirm.confirmLabel}
                    destructive={pendingConfirm.destructive}
                    onConfirm={() => { const c = pendingConfirm; setPendingConfirm(null); c.onConfirm(); }}
                    onCancel={() => setPendingConfirm(null)}
                />
            )}
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <div className="text-sm font-medium truncate">
                        {contact.displayName || <span className="text-muted-foreground italic">(no name)</span>}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{contact.email}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                        Requested {formatRelative(contact.createdUtc)}
                    </div>
                </div>
            </div>

            <div className="flex items-end gap-2 flex-wrap">
                <div className="flex-1 min-w-[200px] space-y-1">
                    <div className="flex items-center gap-2">
                        <Label className="text-xs">Link to</Label>
                        <KindPicker value={kind} onChange={setKind} disabled={busy} />
                    </div>
                    {kind === 'Carrier' ? (
                        <CarrierPicker
                            carriers={carriers}
                            value={chosenCarrierId}
                            onChange={setChosenCarrierId}
                            getToken={getToken}
                            onCarriersChanged={onCarriersChanged}
                            disabled={busy}
                        />
                    ) : (
                        <ClientPicker clients={clients} value={chosenClient} onChange={setChosenClient} disabled={busy} />
                    )}
                </div>
                <Button
                    onClick={approve}
                    disabled={busy || !chosen}
                    size="sm"
                >
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                    Approve
                </Button>
                <Button
                    onClick={reject}
                    disabled={busy}
                    size="sm"
                    variant="outline"
                >
                    <X className="h-3.5 w-3.5" />
                    Reject
                </Button>
            </div>

            {error && (
                <div className="text-xs text-pipeline-rose-text bg-pipeline-rose-bg border border-pipeline-rose-border rounded p-2">
                    {error}
                </div>
            )}
        </div>
    );
}

// =========================================================================
// Approved Carriers panel
// =========================================================================

function ApprovedPanel({
    approved,
    getToken,
    onChanged,
    onMail,
}: {
    approved: CarrierContactRow[] | null;
    getToken: () => Promise<string>;
    onChanged: () => Promise<void>;
    onMail: (email: string, sent: boolean | undefined, what: string) => void;
}) {
    const [busyId, setBusyId] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null);
    const [editingPhoneId, setEditingPhoneId] = useState<number | null>(null);
    const [phoneDraft, setPhoneDraft] = useState('');
    const [phoneBusy, setPhoneBusy] = useState(false);

    const runDisable = async (contact: CarrierContactRow) => {
        setBusyId(contact.carrierContactId);
        setError(null);
        try {
            const token = await getToken();
            await disableCarrierContact(token, contact.carrierContactId);
            await onChanged();
        } catch (e: any) {
            setError(e.message ?? 'Disable failed');
        } finally {
            setBusyId(null);
        }
    };
    /** ⭐ F221: email this user a fresh welcome + sign-in link */
    const [mailBusyId, setMailBusyId] = useState<number | null>(null);
    const emailLink = async (contact: CarrierContactRow) => {
        setMailBusyId(contact.carrierContactId);
        setError(null);
        try {
            const token = await getToken();
            const r = await sendCarrierContactLink(token, contact.carrierContactId);
            onMail(contact.email, r.emailSent, `Sign-in link for ${linkedTo(contact)}`);
        } catch (e: any) {
            onMail(contact.email, false, `Sign-in link for ${linkedTo(contact)}`);
            setError(e.message ?? 'The email could not be sent');
        } finally {
            setMailBusyId(null);
        }
    };
    const disable = (contact: CarrierContactRow) => setPendingConfirm({
        title: 'Disable portal user?',
        message: `Disable ${contact.email} for ${linkedTo(contact)}? They lose this account until it is reactivated under Disabled Portal Users. Their other portal accounts are not touched.`,
        confirmLabel: 'Disable',
        destructive: true,
        onConfirm: () => runDisable(contact),
    });

    const startEditPhone = (contact: CarrierContactRow) => {
        setEditingPhoneId(contact.carrierContactId);
        setPhoneDraft(contact.phone ?? '');
    };
    const savePhone = async (contact: CarrierContactRow) => {
        setPhoneBusy(true);
        setError(null);
        try {
            const token = await getToken();
            await updateCarrierContactPhone(token, contact.carrierContactId, phoneDraft.trim() || null);
            setEditingPhoneId(null);
            await onChanged();
        } catch (e: any) {
            setError(e.message ?? 'Failed to update phone');
        } finally {
            setPhoneBusy(false);
        }
    };

    return (
        <div>
            {pendingConfirm && (
                <ConfirmDialog
                    open={true}
                    title={pendingConfirm.title}
                    message={pendingConfirm.message}
                    confirmLabel={pendingConfirm.confirmLabel}
                    destructive={pendingConfirm.destructive}
                    onConfirm={() => { const c = pendingConfirm; setPendingConfirm(null); c.onConfirm(); }}
                    onCancel={() => setPendingConfirm(null)}
                />
            )}
            {error && (
                <div className="text-sm rounded-md p-3 mb-3 bg-pipeline-rose-bg text-pipeline-rose-text border border-pipeline-rose-border flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                </div>
            )}
            <h3 className="font-medium text-sm mb-3 flex items-center gap-2">
                Approved Portal Users
                {approved && (
                    <span className="text-xs text-muted-foreground font-normal">
                        ({approved.length})
                    </span>
                )}
            </h3>

            {approved === null ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-3">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading...
                </div>
            ) : approved.length === 0 ? (
                <div className="text-sm text-muted-foreground py-3 px-3 border rounded-md">
                    No approved portal users yet.
                </div>
            ) : (
                <div className="border rounded-md divide-y">
                    {approved.map((c) => (
                        <div key={c.carrierContactId} className="flex items-center justify-between gap-3 px-3 py-2.5">
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium truncate">
                                    {c.displayName || c.email}
                                </div>
                                <div className="text-xs text-muted-foreground truncate">
                                    {c.email} &middot; {linkedTo(c)}
                                </div>
                                <div className="text-[11px] text-muted-foreground mt-0.5">
                                    Last login: {c.lastLoginUtc ? formatRelative(c.lastLoginUtc) : 'never'}
                                </div>
                                {editingPhoneId === c.carrierContactId ? (
                                    <div className="flex items-center gap-1.5 mt-1.5">
                                        <input
                                            type="tel"
                                            value={phoneDraft}
                                            onChange={(e) => setPhoneDraft(e.target.value)}
                                            placeholder="+1 555 555 5555"
                                            autoFocus
                                            className="text-xs rounded border border-gray-300 px-2 py-1 w-40 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                        />
                                        <Button size="sm" variant="default" onClick={() => savePhone(c)} disabled={phoneBusy}>
                                            {phoneBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                                        </Button>
                                        <Button size="sm" variant="ghost" onClick={() => setEditingPhoneId(null)} disabled={phoneBusy}>
                                            <X className="h-3 w-3" />
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-1.5 mt-1 text-[11px] text-muted-foreground">
                                        <span>{c.phone ? `Phone: ${c.phone}` : 'No phone on file'}</span>
                                        <button
                                            type="button"
                                            onClick={() => startEditPhone(c)}
                                            className="text-muted-foreground hover:text-foreground"
                                            aria-label="Edit phone"
                                        >
                                            <Pencil className="h-3 w-3" />
                                        </button>
                                    </div>
                                )}
                            </div>
                            <Button
                                onClick={() => emailLink(c)}
                                disabled={mailBusyId === c.carrierContactId}
                                size="sm"
                                variant="outline"
                                title="Email them a welcome with a sign-in link"
                            >
                                {mailBusyId === c.carrierContactId ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                    <Mail className="h-3.5 w-3.5" />
                                )}
                                Email link
                            </Button>
                            <Button
                                onClick={() => disable(c)}
                                disabled={busyId === c.carrierContactId}
                                size="sm"
                                variant="outline"
                            >
                                {busyId === c.carrierContactId ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                    <X className="h-3.5 w-3.5" />
                                )}
                                Disable
                            </Button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// =========================================================================
// ⭐ F215: Disabled Portal Users, with Reactivate
// =========================================================================

/**
 * Every disabled portal user this scheduler may see (the server leaves out another company's
 * client users). Reactivate turns the row back on as it was: an approved account signs in
 * again; a sign-up that was rejected before approval goes back to Pending Approvals.
 */
function DisabledPanel({
    disabled,
    getToken,
    onChanged,
}: {
    disabled: CarrierContactRow[] | null;
    getToken: () => Promise<string>;
    onChanged: () => Promise<void>;
}) {
    const [open, setOpen] = useState(false);
    const [busyId, setBusyId] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);

    const runReactivate = async (contact: CarrierContactRow) => {
        setBusyId(contact.carrierContactId);
        setError(null);
        try {
            const token = await getToken();
            await reactivateCarrierContact(token, contact.carrierContactId);
            await onChanged();
        } catch (e: any) {
            setError(e.message ?? 'Reactivate failed');
        } finally {
            setBusyId(null);
        }
    };

    const count = disabled?.length ?? 0;
    return (
        <div>
            {error && (
                <div className="text-sm rounded-md p-3 mb-3 bg-pipeline-rose-bg text-pipeline-rose-text border border-pipeline-rose-border flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                </div>
            )}
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                className="font-medium text-sm mb-3 flex items-center gap-2 hover:text-foreground"
            >
                <span aria-hidden="true">{open ? '▾' : '▸'}</span>
                Disabled Portal Users
                {disabled && (
                    <span className="text-xs text-muted-foreground font-normal">({count})</span>
                )}
            </button>

            {open && (disabled === null ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-3">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading...
                </div>
            ) : count === 0 ? (
                <div className="text-sm text-muted-foreground py-3 px-3 border rounded-md">
                    No disabled portal users.
                </div>
            ) : (
                <div className="border rounded-md divide-y">
                    {disabled.map((c) => (
                        <div key={c.carrierContactId} className="flex items-center justify-between gap-3 px-3 py-2.5">
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium truncate text-muted-foreground">
                                    {c.displayName || c.email}
                                </div>
                                <div className="text-xs text-muted-foreground truncate">
                                    {c.email} &middot; {c.isApproved ? linkedTo(c) : 'sign-up, never approved'}
                                </div>
                                <div className="text-[11px] text-muted-foreground mt-0.5">
                                    Last login: {c.lastLoginUtc ? formatRelative(c.lastLoginUtc) : 'never'}
                                </div>
                            </div>
                            <Button
                                onClick={() => runReactivate(c)}
                                disabled={busyId === c.carrierContactId}
                                size="sm"
                                variant="outline"
                            >
                                {busyId === c.carrierContactId ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                    <RotateCcw className="h-3.5 w-3.5" />
                                )}
                                Reactivate
                            </Button>
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
}

// =========================================================================
// Reusable: Carrier picker with inline "+ Create new" flow
// =========================================================================

const CREATE_NEW = -1;

function CarrierPicker({
    carriers,
    value,
    onChange,
    getToken,
    onCarriersChanged,
    disabled,
}: {
    carriers: CarrierAdminRow[];
    value: number | null;
    onChange: (id: number | null) => void;
    getToken: () => Promise<string>;
    onCarriersChanged: () => Promise<void>;
    disabled?: boolean;
}) {
    const [createMode, setCreateMode] = useState(false);
    const [newCode, setNewCode] = useState('');
    const [newName, setNewName] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSelect = (v: string) => {
        const id = parseInt(v, 10);
        if (id === CREATE_NEW) {
            setCreateMode(true);
        } else {
            onChange(id);
        }
    };

    const submitNew = async () => {
        if (!newCode.trim() || !newName.trim()) {
            setError('Carrier code and name are both required.');
            return;
        }
        setBusy(true);
        setError(null);
        try {
            const token = await getToken();
            const newCarrierId = await adminCreateCarrier(token, {
                carrierCode: newCode.trim(),
                carrierName: newName.trim(),
            });
            await onCarriersChanged();
            onChange(newCarrierId);
            setCreateMode(false);
            setNewCode('');
            setNewName('');
        } catch (e: any) {
            setError(e.message ?? 'Failed to create carrier');
        } finally {
            setBusy(false);
        }
    };

    const cancelCreate = () => {
        setCreateMode(false);
        setNewCode('');
        setNewName('');
        setError(null);
    };

    if (createMode) {
        return (
            <div className="border rounded-md p-3 bg-muted/30 space-y-2">
                <div className="text-xs font-medium">Create New Carrier</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                        <Label htmlFor="new-carrier-code" className="text-[11px]">Code</Label>
                        <Input
                            id="new-carrier-code"
                            value={newCode}
                            onChange={(e) => setNewCode(e.target.value)}
                            placeholder="ACME"
                            disabled={busy}
                            className="h-8 text-xs"
                        />
                    </div>
                    <div>
                        <Label htmlFor="new-carrier-name" className="text-[11px]">Name</Label>
                        <Input
                            id="new-carrier-name"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            placeholder="Acme Trucking, Inc."
                            disabled={busy}
                            className="h-8 text-xs"
                        />
                    </div>
                </div>
                {error && (
                    <div className="text-[11px] text-pipeline-rose-text">{error}</div>
                )}
                <div className="flex justify-end gap-2">
                    <Button onClick={cancelCreate} disabled={busy} size="sm" variant="ghost">
                        Cancel
                    </Button>
                    <Button onClick={submitNew} disabled={busy} size="sm">
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                        Create &amp; Select
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <Select
            value={value !== null ? String(value) : ''}
            onValueChange={handleSelect}
            disabled={disabled}
        >
            <SelectTrigger>
                <SelectValue placeholder="Select a carrier..." />
            </SelectTrigger>
            <SelectContent>
                {carriers.map((c) => (
                    <SelectItem key={c.CarrierId} value={String(c.CarrierId)}>
                        {c.CarrierCode} &mdash; {c.CarrierName}
                    </SelectItem>
                ))}
                <SelectItem value={String(CREATE_NEW)}>
                    <span className="flex items-center gap-1 text-foreground/70">
                        <Plus className="h-3 w-3" />
                        Create new Carrier...
                    </span>
                </SelectItem>
            </SelectContent>
        </Select>
    );
}

// =========================================================================
// Helpers
// =========================================================================

function formatRelative(isoUtc: string): string {
    const d = new Date(isoUtc);
    const diffMs = Date.now() - d.getTime();
    const diffMin = Math.floor(diffMs / 60_000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 30) return `${diffDay}d ago`;
    return d.toLocaleDateString();
}

// =========================================================================
// Reactivation Requests panel (Phase 5b)
// =========================================================================

function ReactivationRequestsPanel({
    requests,
    getToken,
    onChanged,
}: {
    requests: ReactivationRequest[] | null;
    getToken: () => Promise<string>;
    onChanged: () => Promise<void>;
}) {
    // We only render the panel when there ARE requests. An empty state would
    // be noise alongside the always-empty pending list at most times. If a
    // scheduler wants to confirm "nothing to review", the absence is the
    // signal — combined with the Pending panel which already shows count.
    if (requests === null) return null;
    if (requests.length === 0) return null;

    return (
        <div>
            <h3 className="font-medium text-sm mb-3 flex items-center gap-2">
                <Shield className="h-4 w-4 text-pipeline-amber-text" />
                Reactivation Requests
                <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-pipeline-amber-text text-white text-[10px] font-semibold">
                    {requests.length}
                </span>
            </h3>

            <p className="text-xs text-muted-foreground mb-3">
                Disabled or blocked carriers who recently tried to sign in. Review and decide.
            </p>

            <div className="border rounded-md divide-y">
                {requests.map((r) => (
                    <ReactivationRequestRow
                        key={r.carrierContactId}
                        request={r}
                        getToken={getToken}
                        onChanged={onChanged}
                    />
                ))}
            </div>
        </div>
    );
}

function ReactivationRequestRow({
    request,
    getToken,
    onChanged,
}: {
    request: ReactivationRequest;
    getToken: () => Promise<string>;
    onChanged: () => Promise<void>;
}) {
    const [busy, setBusy] = useState<null | 'reactivate' | 'dismiss' | 'block'>(null);
    const [error, setError] = useState<string | null>(null);
    const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null);

    const exec = async (
        action: 'reactivate' | 'dismiss' | 'block',
        op: (token: string) => Promise<void>,
    ) => {
        setBusy(action);
        setError(null);
        try {
            const token = await getToken();
            await op(token);
            await onChanged();
        } catch (e: any) {
            setError(e.message ?? 'Action failed');
        } finally {
            setBusy(null);
        }
    };

    const run = (
        action: 'reactivate' | 'dismiss' | 'block',
        confirmMsg: string | null,
        op: (token: string) => Promise<void>,
    ) => {
        if (confirmMsg) {
            setPendingConfirm({
                title: action === 'block' ? 'Block carrier?' : action === 'reactivate' ? 'Reactivate carrier?' : 'Please confirm',
                message: confirmMsg,
                confirmLabel: action === 'block' ? 'Block' : action === 'reactivate' ? 'Reactivate' : 'Confirm',
                destructive: action !== 'reactivate',
                onConfirm: () => exec(action, op),
            });
        } else {
            void exec(action, op);
        }
    };

    const handleReactivate = () =>
        run(
            'reactivate',
            `Reactivate ${request.email}? They will be able to sign in again on their next attempt.`,
            (t) => reactivateFromRequest(t, request.carrierContactId),
        );

    const handleDismiss = () =>
        run(
            'dismiss',
            null,  // No confirm for dismiss — it's reversible (a new attempt will resurface them)
            (t) => dismissReactivationRequest(t, request.carrierContactId),
        );

    const handleBlock = () =>
        run(
            'block',
            `Permanently block ${request.email}? Future sign-in attempts will no longer appear in this list.\n\nThis is hard to reverse — only block if you're confident this email should never have access.`,
            (t) => blockFromRequest(t, request.carrierContactId),
        );

    return (
        <div className="px-3 py-3 space-y-2">
            {pendingConfirm && (
                <ConfirmDialog
                    open={true}
                    title={pendingConfirm.title}
                    message={pendingConfirm.message}
                    confirmLabel={pendingConfirm.confirmLabel}
                    destructive={pendingConfirm.destructive}
                    onConfirm={() => { const c = pendingConfirm; setPendingConfirm(null); c.onConfirm(); }}
                    onCancel={() => setPendingConfirm(null)}
                />
            )}
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate flex items-center gap-2">
                        {request.displayName || request.email}
                        {request.isBlocked && (
                            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-pipeline-rose-bg text-pipeline-rose-text border border-pipeline-rose-border">
                                <Ban className="h-2.5 w-2.5" />
                                Blocked
                            </span>
                        )}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                        {request.email}
                        {request.carrierCode && <> &middot; {request.carrierCode}</>}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1">
                        <b>{request.attemptCount}</b> attempt{request.attemptCount === 1 ? '' : 's'}
                        {' '}&middot; latest {formatRelative(request.lastAttemptUtc)}
                        {request.distinctIps.length > 0 && (
                            <>
                                {' '}&middot; from {request.distinctIps.length} IP{request.distinctIps.length === 1 ? '' : 's'}
                                <span className="ml-1 text-muted-foreground/70">
                                    ({request.distinctIps.slice(0, 3).join(', ')}
                                    {request.distinctIps.length > 3 && '…'})
                                </span>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
                {/* Reactivate only makes sense if the contact is currently disabled
                    (not blocked-only). For blocked rows we omit it — to reactivate
                    you'd unblock first, which is a separate manual flow. */}
                {!request.isBlocked && !request.contactIsActive && (
                    <Button
                        onClick={handleReactivate}
                        disabled={busy !== null}
                        size="sm"
                    >
                        {busy === 'reactivate' ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <RotateCcw className="h-3.5 w-3.5" />
                        )}
                        Reactivate
                    </Button>
                )}
                <Button
                    onClick={handleDismiss}
                    disabled={busy !== null}
                    size="sm"
                    variant="outline"
                >
                    {busy === 'dismiss' ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                        <X className="h-3.5 w-3.5" />
                    )}
                    Dismiss
                </Button>
                {!request.isBlocked && (
                    <Button
                        onClick={handleBlock}
                        disabled={busy !== null}
                        size="sm"
                        variant="outline"
                        className="text-pipeline-rose-text hover:text-pipeline-rose-text"
                    >
                        {busy === 'block' ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <Ban className="h-3.5 w-3.5" />
                        )}
                        Block Permanently
                    </Button>
                )}
            </div>

            {error && (
                <div className="text-xs text-pipeline-rose-text bg-pipeline-rose-bg border border-pipeline-rose-border rounded p-2">
                    {error}
                </div>
            )}
        </div>
    );
}
